import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PricingGrid } from "@/pages/public/Pricing";
import { fmtDate, fmtTime } from "@/components/SessionCard";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Ticket, CalendarPlus, Download, AlertTriangle, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { googleCalUrl, downloadIcs } from "@/lib/calendarExport";
import { WorkoutContentButton } from "@/components/WorkoutContentDialog";

export function Credits() {
  const { t, tl, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState([]);
  const [products, setProducts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [subs, setSubs] = useState([]);
  const [buyProduct, setBuyProduct] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const needType = searchParams.get("need");
  const clearNeed = () => setSearchParams({}, { replace: true });
  const shopRef = useRef(null);
  useEffect(() => {
    if (!needType) return;
    const id = setTimeout(() => shopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
    return () => clearTimeout(id);
  }, [needType]);
  const [method, setMethod] = useState("paguelo_facil");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [openUsageId, setOpenUsageId] = useState(null);
  const [usage, setUsage] = useState({}); // credit_id -> [] | "loading"

  const load = async () => {
    const [c, r, s, p] = await Promise.all([
      api.get("/me/credits"), api.get("/me/reservations"),
      api.get("/me/subscriptions"), api.get("/products"),
    ]);
    setCredits(c.data);
    setReservations(r.data.filter((x) => x.status === "confirmed"));
    setSubs(s.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const rebuy = (credit) => {
    const p = products.find((x) => x.product_id === credit.product_id);
    if (!p) { toast.error(tl({ es: "Producto no disponible", en: "Product unavailable", fr: "Produit indisponible" })); return; }
    setBuyProduct(p); setMethod("paguelo_facil");
  };

  const toggleUsage = async (credit) => {
    if (openUsageId === credit.credit_id) { setOpenUsageId(null); return; }
    setOpenUsageId(credit.credit_id);
    if (!usage[credit.credit_id]) {
      setUsage((u) => ({ ...u, [credit.credit_id]: "loading" }));
      try {
        const { data } = await api.get(`/me/credits/${credit.credit_id}/usage`);
        setUsage((u) => ({ ...u, [credit.credit_id]: data }));
      } catch { setUsage((u) => ({ ...u, [credit.credit_id]: [] })); }
    }
  };

  const toggleFreeze = async (sub) => {
    try {
      const action = sub.status === "frozen" ? "unfreeze" : "freeze";
      await api.post(`/me/subscriptions/${sub.subscription_id}/${action}`);
      toast.success(t("common.success"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const SUB_STATUS = {
    active: { es: "Activa", en: "Active", fr: "Active" },
    frozen: { es: "En pausa", en: "Paused", fr: "En pause" },
    cancelled: { es: "Cancelada", en: "Cancelled", fr: "Annulée" },
    expired: { es: "Expirada", en: "Expired", fr: "Expirée" },
  };

  // Handle Paguelo Fácil return: ?payment=pay_xxx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("payment");
    if (!pid) return;
    window.history.replaceState(null, "", "/app/credits");
    api.get(`/payments/${pid}/status`).then(({ data }) => {
      if (data.status === "paid") toast.success("¡Pago confirmado! Tus créditos están activos.");
      else toast.message("Tu pago está en proceso. Actualizaremos tus créditos al confirmarse.");
      load();
    }).catch(() => {});
  }, []);

  const CREDIT_LABEL = {
    fitness: "Fitness", youth: "Teens & Kids", youth_baby: "Baby (3-5)", youth_kids: "Kids (6-11)", youth_teens: "Teens (12-17)",
    calisthenics: "Calisthenics", private: "Fitness privado",
    pilates_group: "Pilates grupal", pilates_private: "Pilates privado",
  };

  const purchase = async () => {
    try {
      const { data } = await api.post(`/products/${buyProduct.product_id}/purchase`, { product_id: buyProduct.product_id, method });
      setBuyProduct(null);
      if (data.checkout_url) {
        window.location.href = data.checkout_url; // Paguelo Fácil offsite checkout
        return;
      }
      if (data.requires_manual_confirmation) {
        toast.success("Pago registrado. El estudio lo confirmará y activará tus créditos.");
      } else {
        toast.success(t("common.success"));
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "billing_id_required") {
        toast.error(
          tl({ es: "Completa tu documento (Cédula/RUC/Pasaporte) para facturar.", en: "Add your document (ID/RUC/Passport) for billing.", fr: "Complète ton document (Cédula/RUC/Passeport) pour la facturation." }),
          { action: { label: tl({ es: "Completar", en: "Complete", fr: "Compléter" }), onClick: () => navigate("/app/profile") } }
        );
      } else {
        toast.error(formatApiError(d));
      }
    }
  };

  const openCancel = async (res) => {
    setCancelTarget(res);
    try {
      const { data } = await api.get(`/reservations/${res.reservation_id}/cancel-preview`);
      setPreview(data);
    } catch { setPreview(null); }
  };

  const doCancel = async () => {
    try {
      await api.post(`/reservations/${cancelTarget.reservation_id}/cancel`);
      toast.success(t("booking.cancelled"));
      setCancelTarget(null); setPreview(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && d.code === "too_late_to_cancel") {
        toast.error(tl({
          es: `No puedes cancelar a menos de ${d.hours_limit}h del inicio de la clase.`,
          en: `You can't cancel within ${d.hours_limit}h of the class start.`,
          fr: `Tu ne peux pas annuler à moins de ${d.hours_limit}h du début du cours.`,
        }));
        setCancelTarget(null); setPreview(null);
      } else {
        toast.error(formatApiError(d));
      }
    }
  };

  const active = useMemo(() => credits.filter((c) => c.status === "active"), [credits]);
  const activeSubs = useMemo(() => subs.filter((s) => s.status !== "cancelled"), [subs]);
  // Map a credit to its (non-cancelled) subscription so we can hide session counts for subscriptions.
  const subByCredit = useMemo(() => {
    const m = {};
    subs.forEach((s) => { if (s.credit_id && s.status !== "cancelled") m[s.credit_id] = s; });
    return m;
  }, [subs]);

  const resCal = (r) => ({
    title: `${tl(r.session?.class_type?.name) || "Frenchies"} — ${r.session?.universe === "pilates" ? "Pilates" : "Fitness"}`,
    start: r.session?.start,
    end: r.session?.end,
    details: [r.session?.coach?.name && `Coach: ${r.session.coach.name}`, "Frenchies Fitness & Pilates"].filter(Boolean).join(" · "),
    location: r.session?.room?.name || "Frenchies",
  });

  return (
    <AppLayout>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">{t("credits.title")}</h1>

      {active.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <Ticket className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">{t("credits.none")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((c) => {
            const sub = subByCredit[c.credit_id];
            const isSub = !!sub;
            const unlimited = c.credits_remaining >= 999 || (c.credits_total || 0) >= 999;
            const total = c.credits_total ?? c.credits_remaining;
            const pct = total > 0 ? Math.min(100, Math.round((c.credits_remaining / total) * 100)) : 0;
            const low = !unlimited && c.credits_remaining <= 1;
            const empty = !unlimited && c.credits_remaining <= 0;
            const hasProduct = !!products.find((x) => x.product_id === c.product_id);
            const usageRows = usage[c.credit_id];
            const consumedRows = Array.isArray(usageRows) ? usageRows.filter((r) => r.consumed) : [];
            const isOpen = openUsageId === c.credit_id;

            // Subscriptions: hide the session count, show only the renewal date + a bin to stop recurrence.
            if (isSub) {
              return (
                <div key={c.credit_id} data-testid={`credit-${c.credit_id}`} className="rounded-2xl border border-border bg-card p-6">
                  <div className="min-w-0">
                    <div className="truncate font-display text-lg font-semibold">{tl(sub.product_name) || (CREDIT_LABEL[c.credit_type] || "")}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {tl({ es: "Renovación", en: "Renews", fr: "Renouvellement" })}: <span className="font-medium text-foreground">{fmtDate(sub.period_end, lang)}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                      {tl({ es: "Suscripción", en: "Subscription", fr: "Abonnement" })} · {CREDIT_LABEL[c.credit_type] || (c.universe_scope || []).join(", ")}
                    </span>
                    {sub.status === "frozen" && (
                      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        {tl({ es: "En pausa", en: "Paused", fr: "En pause" })}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div key={c.credit_id} data-testid={`credit-${c.credit_id}`} className={`rounded-2xl border bg-card p-6 ${low ? "border-amber-400/60" : "border-border"}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-3xl font-bold">{unlimited ? "∞" : c.credits_remaining}</div>
                  {!unlimited && (
                    <div data-testid={`credit-usage-${c.credit_id}`} className="text-sm font-semibold text-foreground">
                      {c.credits_remaining}/{total} <span className="font-normal text-muted-foreground">{tl({ es: "restantes", en: "left", fr: "restantes" })}</span>
                    </div>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">{t("credits.remaining")} · {CREDIT_LABEL[c.credit_type] || (c.universe_scope || []).join(", ")}</div>
                {!unlimited && (
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div className={`h-full rounded-full transition-all ${low ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
                {c.expires_at && <div className="mt-2 text-xs text-muted-foreground">{t("credits.expires")}: {fmtDate(c.expires_at, lang)}</div>}

                {low && (
                  <div data-testid={`credit-low-alert-${c.credit_id}`} className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{empty
                      ? tl({ es: "Bono agotado. ¡Vuelve a comprar para seguir reservando!", en: "Pack used up. Rebuy to keep booking!", fr: "Carte épuisée. Rachète-la pour continuer à réserver !" })
                      : tl({ es: "¡Última sesión! Vuelve a comprar para no quedarte sin cupo.", en: "Last session left! Rebuy so you don't run out.", fr: "Dernière séance ! Rachète ta carte pour ne pas être à court." })}</span>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="ghost" data-testid={`credit-usage-toggle-${c.credit_id}`} onClick={() => toggleUsage(c)}
                    className="h-8 rounded-full px-3 text-xs">
                    {isOpen ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
                    {tl({ es: "Ver uso", en: "View usage", fr: "Voir l'utilisation" })}
                  </Button>
                  {low && hasProduct && (
                    <Button size="sm" data-testid={`credit-rebuy-${c.credit_id}`} onClick={() => rebuy(c)}
                      className="h-8 rounded-full px-3 text-xs">
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      {tl({ es: "Volver a comprar", en: "Rebuy", fr: "Racheter" })}
                    </Button>
                  )}
                </div>

                {isOpen && (
                  <div data-testid={`credit-usage-list-${c.credit_id}`} className="mt-3 border-t border-border pt-3">
                    {usageRows === "loading" ? (
                      <p className="text-xs text-muted-foreground">{t("common.loading")}</p>
                    ) : consumedRows.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{tl({ es: "Aún sin sesiones usadas.", en: "No sessions used yet.", fr: "Aucune séance utilisée pour le moment." })}</p>
                    ) : (
                      <div className="space-y-1.5">
                        {consumedRows.map((r) => (
                          <div key={r.reservation_id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="min-w-0 truncate font-medium">{tl(r.class_name) || r.universe}</span>
                            <span className="shrink-0 text-muted-foreground">{r.start ? `${fmtDate(r.start, lang)} · ${fmtTime(r.start, lang)}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeSubs.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-3 font-display text-lg font-semibold">{tl({ es: "Mis suscripciones", en: "My subscriptions", fr: "Mes abonnements" })}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {activeSubs.map((s) => (
              <div key={s.subscription_id} data-testid={`sub-${s.subscription_id}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display font-semibold">{tl(s.product_name)}</div>
                    <div className="text-xs text-muted-foreground">{tl({ es: "Vence", en: "Renews", fr: "Renouvellement" })}: {fmtDate(s.period_end, lang)}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.status === "frozen" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>
                    {(SUB_STATUS[s.status] || {})[lang] || s.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`freeze-btn-${s.subscription_id}`} onClick={() => toggleFreeze(s)} className="rounded-full">
                    {s.status === "frozen"
                      ? tl({ es: "Reactivar", en: "Resume", fr: "Réactiver" })
                      : tl({ es: "Pausar / Congelar", en: "Pause / Freeze", fr: "Mettre en pause" })}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reservations.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-3 font-display text-lg font-semibold">{t("nav.book")}</h2>
          <div className="space-y-2">
            {reservations.map((r) => (
              <div key={r.reservation_id} data-testid={`reservation-${r.reservation_id}`} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium">{tl(r.session?.class_type?.name)}</div>
                  <div className="text-sm text-muted-foreground">{r.session?.start && `${fmtDate(r.session.start, lang)} · ${fmtTime(r.session.start, lang)}`}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <WorkoutContentButton session={r.session} enrolled />
                  {r.session?.start && r.session?.end && (
                    <>
                      <a href={googleCalUrl(resCal(r))} target="_blank" rel="noopener noreferrer" data-testid={`res-gcal-${r.reservation_id}`}
                        title={t("schedule.googleCal")} className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:bg-secondary">
                        <CalendarPlus className="h-4 w-4" />
                      </a>
                      <button type="button" data-testid={`res-ics-${r.reservation_id}`} onClick={() => downloadIcs(resCal(r))}
                        title={t("schedule.icsFile")} className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:bg-secondary">
                        <Download className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <Button size="sm" variant="outline" data-testid={`cancel-res-${r.reservation_id}`} onClick={() => openCancel(r)} className="rounded-full">{t("common.cancel")}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12" ref={shopRef}>
        <h2 className="mb-5 font-display text-2xl font-bold tracking-tight">{t("credits.buyPack")}</h2>
        <PricingGrid needType={needType} onClearNeed={clearNeed} onBuy={(p) => { setBuyProduct(p); setMethod("paguelo_facil"); }} />
      </div>

      {/* purchase dialog */}
      <Dialog open={!!buyProduct} onOpenChange={(o) => !o && setBuyProduct(null)}>
        <DialogContent>
          {(() => {
            const discPct = Number(user?.discount_percent) || 0;
            const price = Number(buyProduct?.price) || 0;
            const discounted = +(price * (1 - discPct / 100)).toFixed(2);
            return (
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-baseline gap-2">
                  <span>{buyProduct && tl(buyProduct.name)}</span>
                  {discPct > 0 ? (
                    <span data-testid="purchase-discount-price" className="flex items-baseline gap-2">
                      <span className="text-muted-foreground line-through">${price}</span>
                      <span className="text-primary">${discounted}</span>
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">−{discPct}%</span>
                    </span>
                  ) : (
                    <span>— ${price}</span>
                  )}
                </DialogTitle>
                {discPct > 0 && (
                  <p className="text-xs text-muted-foreground">{tl({ es: "Descuento personalizado aplicado.", en: "Personalized discount applied.", fr: "Remise personnalisée appliquée." })}</p>
                )}
              </DialogHeader>
            );
          })()}
          <div className="rounded-xl border border-border p-3 text-sm" data-testid="pay-method-online">
            {tl({ es: "Pago en línea con tarjeta", en: "Online card payment", fr: "Paiement en ligne par carte" })}
          </div>
          <DialogFooter>
            <Button data-testid="confirm-purchase-btn" onClick={purchase} className="rounded-full">{t("pricing.buy")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("booking.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {preview ? (preview.will_lose_credit
                ? tl({ es: `Cancelación fuera de plazo: perderás tu crédito (menos de ${preview.hours_limit}h).`, en: `Late cancellation: you will lose your credit (under ${preview.hours_limit}h).`, fr: `Annulation hors délai : tu perdras ton crédit (moins de ${preview.hours_limit}h).` })
                : tl({ es: "Tu crédito será devuelto.", en: "Your credit will be refunded.", fr: "Ton crédit sera remboursé." })) : ""}
              {preview && <div className="mt-2 text-xs">{t("booking.hoursLeft")}: {preview.hours_left}h</div>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.close")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-cancel-btn" onClick={doCancel}>{t("common.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
